import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarHeader from "./SidebarHeader";
import NavigationItem from "./NavigationItem";
import UserAvatar from "../UserAvatar/UserAvatar";
import useNavigation from "../../../hooks/useNavigation";
import "./SidebarNavigation.css";

function SidebarNavigation({
  isCollapsed = false,
  isMobileOpen = false,
  onToggle,
  onMobileClose,
}) {
  const navigate = useNavigate();
  const { navigationItems, isNavigationItemActive, currentPath, debug } =
    useNavigation();
  const unreadMessageCount = 0;

  // Detect if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggle = () => {
    // On mobile the header button closes the drawer; on desktop it collapses
    if (isMobile) {
      onMobileClose?.();
    } else if (onToggle) {
      onToggle();
    }
  };

  // Handle navigation item clicks with proper active state management
  const handleNavigationClick = navItem => {
    // Navigate to the new route
    navigate(navItem.path);

    // Close the mobile drawer once a page is picked
    if (isMobile) {
      onMobileClose?.();
    }

    // Debug logging in development
    if (process.env.NODE_ENV === "development" && debug) {
      // Navigation clicked: ${navItem.label} to path: ${navItem.path}
      // Log active state after a brief delay to allow route change
      setTimeout(() => debug.logActiveState(), 100);
    }
  };

  // Debug effect to log navigation state changes
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && debug) {
      // SidebarNavigation rendered with path: ${currentPath}
    }
  }, [currentPath, debug]);

  // Determine sidebar classes based on state
  const getSidebarClasses = () => {
    const classes = ["sidebar-navigation"];

    if (isMobile) {
      classes.push("mobile");
      classes.push(isMobileOpen ? "mobile-open" : "mobile-closed");
    } else {
      classes.push(isCollapsed ? "collapsed" : "expanded");
    }

    return classes.join(" ");
  };

  // The mobile drawer always shows full labels
  const showCollapsed = !isMobile && isCollapsed;

  return (
    <>
      {isMobile && isMobileOpen && (
        <div
          className="sidebar-mobile-backdrop"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
      <nav
        className={getSidebarClasses()}
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={isMobile && !isMobileOpen}
      >
        {/* Sidebar Header */}
        <SidebarHeader isCollapsed={showCollapsed} onToggle={handleToggle} />

        {/* Sidebar Menu */}
        <div className="sidebar-menu">
          {navigationItems.map(item => (
            <NavigationItem
              key={item.id}
              item={{
                ...item,
                badgeCount: item.id === "chat" ? unreadMessageCount : 0,
              }}
              isActive={isNavigationItemActive(item)}
              isCollapsed={showCollapsed}
              onClick={handleNavigationClick}
            />
          ))}
        </div>

        {/* Sidebar Footer with User Avatar */}
        <div className="sidebar-footer">
          <UserAvatar
            size="medium"
            showName={!showCollapsed}
            showRole={!showCollapsed}
          />
        </div>
      </nav>
    </>
  );
}

export default SidebarNavigation;
